<script setup>
import { useCommunitiesStore } from '@/stores/communitiesStore'
import { useCommunityCompaniesStore } from '@/stores/communityCompaniesStore'
import { useMeteringPointMembershipsStore } from '@/stores/meteringPointMembershipsStore'
import {computed, inject, ref, watch} from "vue";
import timeTools from "@/lib/es-bill-shared-js/misc/timeTools";
import edaTools from "@/lib/es-bill-shared-js/misc/edaTools";
import { useFrontEndStore } from "@/stores/frontEndStore";
import { getResponseInfoCustomerProcesses } from "@/common/responseCodeResolver";
import {useI18n} from "vue-i18n";
import { startEnergyDataRequest } from '@/services/meteringPointMembershipService';
import { PrimaryButton, SecondaryButton, TextField, DialogFlexibleInput, DateRangePicker, useSnackbarStore } from '@es-bill/es-bill-common-components';

import { DateTime, Settings } from 'luxon';
Settings.defaultZone = 'Europe/Vienna'; // might be redundant

const communitiesStore = useCommunitiesStore();
const companiesStore = useCommunityCompaniesStore();
const mpmStore = useMeteringPointMembershipsStore();
const { t } = useI18n();
const $log = inject('log');
const snackbarStore = useSnackbarStore();

// kinda tricky:
// this view is used as standalone view used by router and also as sub view,
// so the defaults are for the standalone screen
const props = defineProps({
  showHeader: { type: Boolean, required: false, default: true },

  // fixed filters to
  clientCodeFilter: { type: String, required: false },
  //ecidFilter: { type: String, required: false }, // @todo ecid aware

  overwriteFilterAndPaging: {
    type: Object,
    default(rawProps) { return useFrontEndStore().pageSettingsFilter.viewMeteringPoints },
    required: false
  },
  overwriteFilterCompany: {
    type: Object,
    default(rawProps) { return useFrontEndStore().communityCompanyFilter },
    required: false
  },

})

const filterPaging = ref(props.overwriteFilterAndPaging);
const filterCompany = ref(props.overwriteFilterCompany);

function getMembershipsExtended() {
  // Build O(1) lookup maps once instead of three .filter() scans per row.
  // With ~13k MPMs this changes the cost from O(N × |communities + companies + members|) to O(N).
  const communityByEcn = new Map();
  for (const c of communitiesStore.allCommunities) {
    if (c?.ecn) communityByEcn.set(c.ecn, c);
  }
  const companyByEcn = new Map();
  const memberByEcnClientCode = new Map();
  for (const cc of companiesStore.allCommunityCompanies) {
    if (!cc?.ecn) continue;
    companyByEcn.set(cc.ecn, cc);
    for (const m of cc.members || []) {
      if (m?.clientCode) memberByEcnClientCode.set(cc.ecn + '|' + m.clientCode, m);
    }
  }

  let c = [];
  for (const mpm of mpmStore.allMeteringPointMembershipsCm) {
    const community = communityByEcn.get(mpm.ecn);
    const communityCompany = companyByEcn.get(mpm.ecn);
    const member = mpm.clientCode?.length
      ? memberByEcnClientCode.get(mpm.ecn + '|' + mpm.clientCode) || null
      : null;

    const ds = timeTools.formatDate(mpm.dateStart)
    const de = timeTools.formatDate(mpm.dateEnd);
    let dSpan = []
    if(ds.length)
      dSpan.push(ds)
    if(de.length)
      dSpan.push(ds)

    c.push({
      ...mpm,
      memberAssociated: member,
      meteringPointShort: edaTools.shortenIdentifiersLong(mpm.meteringPoint),
      meteringpointDirectionDisplay: mpm.meteringPointDirection === "GENERATION" ? t("producer") : t("consumer"),
      community: community,
      communityCompany: communityCompany,
      member: member,
      dateStartDisplay: ds,
      dateEndDisplay: de,
      dateSpan: dSpan.length ? dSpan.join(` ${t('to')} `) : "",
    });
  }

  if (props.clientCodeFilter) {
    c = c.filter(p => p.clientCode === props.clientCodeFilter);
  }

  return c;
}

const membershipsDisplay = computed(() => {
  const ecnList = filterCompany.value.ecnList;
  const status = filterPaging.value.status;

  $log.debug("membershipsDisplay update I",ecnList,status)

  const mpms = getMembershipsExtended().filter(mpm =>
    (
      ecnList.length === 0 ||
      ecnList.includes(mpm.ecn)
    ) && (
      status.length === 0 ||
      status.includes(mpm.membershipStatus)
    )
  );

  //$log.debug("membershipsDisplay update ",mpms)
  // console.trace();
  return mpms;
})

const tableHeader = computed(() => {
  let cols = [];
  cols.push({ title: t('description'), value: 'nameFriendly', key: 'nameFriendly' })

  if (filterCompany.value.ecnList.length !== 1)
    cols.push({ title: t('serviceProvider'), value: 'communityCompany.name', key: 'communityCompany.name'})

  if (!props.clientCodeFilter) {
    cols.push({ title: t('customer'), value: 'memberAssociated.nameFriendly', key: 'memberAssociated.nameFriendly'})
  }

  cols.push({ title: t('meteringPointNumber'), value: 'meteringPointShort', key: 'meteringPointShort'})
  cols.push({ title: t('direction'), value: 'meteringpointDirectionDisplay', key: 'meteringpointDirectionDisplay' })
  cols.push({ title: t('status'), value: 'membershipStatus', key: 'membershipStatus'})
  cols.push({ title: t('from'), value: 'dateStartDisplay', key: 'dateStartDisplay'})
  cols.push({ title: t('to'), value: 'dateEndDisplay', key: 'dateEndDisplay' })
  cols.push({ title: t('actions'), key: 'actions', sortable: false, align: 'd-none' });

  cols = cols.concat([
    { text: 'clientCode', value: 'clientCode', align: ' d-none' }, // ' d-none' hides the column but keeps the search ability
    { text: 'meteringPoint', value: 'meteringPoint', align: ' d-none' },
  ])

  return cols;
});

async function handleRowClick(event, row) {
  // await router.push({ name: 'ViewMeteringpointMembership', params: { mpmObjectId: row.item._id } })

}
async function handleCardClick(item, event) {
  // await router.push({ name: 'ViewMeteringpointMembership', params: { mpmObjectId: item._id } })
}

const communityAvailable = computed(() => {
  // prefilter relevant
  const cs = companiesStore.allCommunityCompanies.filter(c => c.ecn.startsWith("EP") || c.ecn === "CC101073");

  return cs.map(c => {
    return {
      title: c.name,
      props: { subtitle: c.ecn },
      ecn: c.ecn,
    };
  })

})


const statusAvailable = ref([
  { title: t("created"), value: "CREATED" },
  { title: t("started"), value: "STARTED" },
  { title: t("declined"), value: "DECLINED" },
  { title: t("waitingForUser"), value: "WAITING_FOR_USER" },
  { title: t("accepted"), value: "ACCEPTED" },
  { title: t("active"), value: "ACTIVE" },
  { title: t("ending"), value: "ENDING" },
  { title: t("ended"), value: "ENDED" },
]);

function getChipColor(v) {
  if (v === "CREATED")
    return "#1C7BCB";
  if (v === "STARTED")
    return "orange"; // FAC934 fde59a
  if (v === "DECLINED")
    return "red";
  if (v === "WAITING_FOR_USER")
    return "orange";
  if (v === "ACCEPTED")
    return "orange";
  if (v === "ACTIVE")
    return "#459506";
  if (v === "ENDING")
    return "#459506";
  if (v === "ENDED")
    return "#333333";

  return "#333333";
}

function getChipText(v) {
  if (v === "CREATED")
    return t("created");
  if (v === "STARTED")
    return t("started");
  if (v === "DECLINED")
    return t("declined");
  if (v === "WAITING_FOR_USER")
    return t("waitingForUser");
  if (v === "ACCEPTED")
    return t("accepted");
  if (v === "ACTIVE")
    return t("active");
  if (v === "ENDING")
    return t("ending");
  if (v === "ENDED")
    return t("ended");

  return v;
}

function getChipTooltip(v, mpm) {
  if (v === "CREATED")
    return t("meteringPointInfo.createdTooltip");
  if (v === "STARTED")
    return t("meteringPointInfo.startedTooltip");
  if (v === "DECLINED") {
    let msg = t("meteringPointInfo.declinedTooltip");
    if (mpm?.reasonDeclined === 'DECLINED_DSO')
      msg += t("meteringPointInfo.declinedDsoReason");
    if (mpm?.reasonDeclined === 'DECLINED_USER')
      msg += t("meteringPointInfo.declinedUserReason");
    if (mpm?.reasonDeclined === 'TIMEOUT_USER')
      msg += t("meteringPointInfo.timeoutUserReason");
    return msg;
  }
  if (v === "WAITING_FOR_USER")
    return t("meteringPointInfo.waitingForUserTooltip");
  if (v === "ACCEPTED")
    return t("meteringPointInfo.acceptedTooltip");
  if (v === "ACTIVE")
    return t("meteringPointInfo.activeTooltip");
  if (v === "ENDING")
    return t("meteringPointInfo.endingTooltip");
  if (v === "ENDED")
    return t("meteringPointInfo.ended");

  return v;
}

function copyText(txt) {
  navigator.clipboard.writeText(txt);
}

const overlayInfoActive = ref(false);
const overlayInfoMessage = ref("");
const overlayButtons = computed(() => [
  {
    component: PrimaryButton,
    props: { size: 'large', class: 'w-100', text: t('ok') },
    cols: '12',
    md: '12',
    onClick: () => { overlayInfoActive.value = false; }
  }
]);

function showDetails(mpm) {
  if (!mpm || mpm.membershipStatus !== "DECLINED")
    return false;

  let msg = t("declinedTooltipDetailed");
  if (mpm?.reasonDeclined === 'DECLINED_DSO')
    msg += t("declinedDsoReason") + "<br>";
  if (mpm?.reasonDeclined === 'DECLINED_USER')
    msg += t("declinedUserReason") + "<br>";
  if (mpm?.reasonDeclined === 'TIMEOUT_USER')
    msg += t("timeoutUserReason") + "<br>";

  msg += t("detailInfo") + "<br>";
  for (const responseCode of mpm.responseCodes) {
    msg += responseCode + ": " + getResponseInfoCustomerProcesses(responseCode).description + "<br>";
  }

  overlayInfoMessage.value = msg;
  overlayInfoActive.value = true
  return true;
}

// Checkbox implementation
/**
 * Watch checkboxes and log selected metering points id's.
 *
 * @type {ref<[]>}
 */
const selectedMeteringPoints = ref([]);
watch(() => selectedMeteringPoints.value, (newValue, oldValue) => {
  $log.debug('Selected metering points:', newValue);
});

// Button implementation
const selectDateDialog = ref(false); // True opens select-date dialog
const jsonInputDialog = ref(false);
const jsonInput = ref('');
const jsonValidationError = ref('');
const jsonSummaryDialog = ref(false);
const jsonSummaryData = ref(null);

/**
 * Unified cancel function for all dialogs
 *
 * @param {string} dialogType - Type of dialog to cancel ('selectDate', 'confirmEnergy', 'jsonInput', 'jsonSummary')
 * @returns {void}
 */
function cancelDialog(dialogType) {
  switch (dialogType) {
    case 'selectDate':
      selectDateDialog.value = false;
      energyDataDateRange.value = [];
      break;
    case 'confirmEnergy':
      confirmGetEnergyDataDialog.value = false;
      energyDataDateRange.value = [];
      break;
    case 'jsonInput':
      jsonInputDialog.value = false;
      jsonInput.value = '';
      jsonValidationError.value = '';
      break;
    case 'jsonSummary':
      jsonSummaryDialog.value = false;
      jsonSummaryData.value = null;
      break;
  }
}

/**
 * Get energy data button process.
 *
 * Check at least one metering point is selected.
 * Start select date dialog.
 * If no meter point is selected use overlay to show error message.
 *
 * @returns {void}
 */
async function buttonGetEnergyData() {
  if (selectedMeteringPoints.value.length === 0) {
    snackbarStore.showSnackbar(t('getEnergyData.noMpError'), t('getEnergyData.noMeterPoint'), 'Error', 5000, []);
  }
  else {
    selectDateDialog.value = !selectDateDialog.value;
  }
}

async function jsonButtonGetEnergyData() {
  jsonInputDialog.value = !jsonInputDialog.value;
  console.log('jsonGetEnergyData');
}

/**
 * Process JSON input for energy data requests.
 *
 * Validates the JSON structure and each entry.
 * Tracks metering points that were not found in the system.
 * Shows a summary dialog with valid entries and not found metering points.
 *
 */
async function processJson() {
  try {
    // Parse JSON
    const data = JSON.parse(jsonInput.value);

    // Structure validation - check if it's an array
    if (!Array.isArray(data)) {
      throw new Error(t('getEnergyData.invalidJsonStructure'));
    }

    // Process JSON and track not found metering points
    const requestDataArray = [];
    const notFoundMeteringPoints = [];

    // Validate each entry
    for (const mp of data) {
      // Required fields check
      if (!mp.meteringPoint || !mp.dateFrom || !mp.dateTo || !mp.ecn || !mp.ecid) {
        throw new Error(t('getEnergyData.missingRequiredFields'));
      }

      // Date validation
      const startDate = new Date(mp.dateFrom);
      const endDate = new Date(mp.dateTo);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error(t('getEnergyData.invalidDates'));
      }

      if (startDate > endDate) {
        throw new Error(t('getEnergyData.startDateAfterEndDate'));
      }

      // Future date validation
      if (startDate > new Date() || endDate > new Date()) {
        throw new Error(t('getEnergyData.futureDate'));
      }

      // Try to find matching membership
      const membership = getMembershipsExtended().find(
        m => m.meteringPoint === mp.meteringPoint &&
            m.ecn === mp.ecn &&
            m.ecid === mp.ecid
      );

      if (membership) {
        requestDataArray.push({
          meteringPointsData: [membership],
          dateStart: new Date(mp.dateFrom),
          dateEnd: new Date(mp.dateTo)
        });
      } else {
        // Add to not found list
        notFoundMeteringPoints.push({
          meteringPoint: mp.meteringPoint,
          ecn: mp.ecn,
          ecid: mp.ecid
        });

        $log.warn(`No matching membership found for metering point ${mp.meteringPoint} with ECN ${mp.ecn} and ECID ${mp.ecid}`);
      }
    }

    // Validate results
    if (requestDataArray.length === 0) {
      throw new Error(t('getEnergyData.noValidMeteringPoints'));
    }

    // Close input dialog and show summary
    jsonInputDialog.value = false;
    jsonSummaryData.value = {
      validEntries: requestDataArray,
      notFoundMeteringPoints: notFoundMeteringPoints
    };
    jsonSummaryDialog.value = true;

  } catch (error) {
    jsonValidationError.value = error.message;
  }
}

async function confirmJsonSummary() {
  try {

    for (const requestData of jsonSummaryData.value.validEntries) {

      // Debug logging
      // $log.debug('Processing energy data request:', {
      //   meteringPoints: requestData.meteringPointsData.map(mp => mp.meteringPointShort),
      //   dateStart: requestData.dateStart,
      //   dateEnd: requestData.dateEnd
      // });

      const callsResult = await startEnergyDataRequest(requestData);
      $log.debug('Energy data request results:', callsResult);
    }

    // Close summary dialog
    jsonSummaryDialog.value = false;
    jsonSummaryData.value = null;
  } catch (error) {
    $log.error('Error processing JSON data:', error);
  }
}

// Date Picker
const energyDataDateRange = ref([]);
const getEnergyDataStartDate = computed(() => {
  if (!energyDataDateRange.value || energyDataDateRange.value.length === 0) return null;
  else
    return new Date(Math.min(...energyDataDateRange.value));
})
const getEnergyDataEndDate = computed(() => {
  if (!energyDataDateRange.value || energyDataDateRange.value.length === 0) return null;

  // always use start of day (inclusive) for last date
  const de = new Date(Math.max(...energyDataDateRange.value));
  return DateTime.fromJSDate(de).startOf('day').toJSDate();
})

/**
 * Check date range, start get data process.
 *
 * Check if end date is after start date, if not use overlay
 * to show error message.
 * Open data summary.
 *
 *
 * @returns {void}
 */
async function confirmGetEnergyDates() {
  // Check dates selected
  if (!energyDataDateRange.value || energyDataDateRange.value.length === 0 ) {
    snackbarStore.showSnackbar(t('getEnergyData.dateError'), t('getEnergyData.noDate'), 'Error', 5000, []);
  }
  // Dates in future not allowed
  else if (getEnergyDataStartDate.value > new Date() || getEnergyDataEndDate.value > new Date()) {
    snackbarStore.showSnackbar(t('getEnergyData.dateError'), t('getEnergyData.futureDate'), 'Error', 5000, []);
  }
  else {
    // Confirm dates show summary
    selectDateDialog.value = false;
    confirmGetEnergyDataDialog.value = true;
  }
}

/**
 * Assign metering points membership data to selected metering points.
 *
 * @returns {Object}
 *  - meteringPointsData: {Array} - Data for selected metering points.
 *  - dateStart: {string} - Start date for energy data, from date picker.
 *  - dateEnd: {string} - End date for energy data, from date picker.
 */
const requestEnergyData = computed( () => {
  const requestData = {
    meteringPointsData: [],
    dateStart: getEnergyDataStartDate.value,
    dateEnd: getEnergyDataEndDate.value
  }
  requestData.meteringPointsData = getMembershipsExtended().filter(mpm =>
    selectedMeteringPoints.value.includes(mpm.mpmObjectId)
  )
  return requestData;
  }
)

// Confirm Dialog
const confirmGetEnergyDataDialog = ref(false);
const dateDialogButtons = computed(() => [
  {
    component: SecondaryButton,
    props: { size: 'large', class: 'w-100', text: t('cancel') },
    cols: '12',
    md: '6',
    onClick: () => { cancelDialog('selectDate'); }
  },
  {
    component: PrimaryButton,
    props: { size: 'large', class: 'w-100', text: t('confirm') },
    cols: '12',
    md: '6',
    onClick: () => { confirmGetEnergyDates(); }
  }
]);
const energySummaryButtons = computed(() => [
  {
    component: SecondaryButton,
    props: { size: 'large', class: 'w-100', text: t('cancel') },
    cols: '12',
    md: '6',
    onClick: () => { cancelDialog('confirmEnergy'); }
  },
  {
    component: PrimaryButton,
    props: { size: 'large', class: 'w-100', text: t('confirm') },
    cols: '12',
    md: '6',
    onClick: () => { confirmGetEnergyData(); }
  }
]);
const jsonInputButtons = computed(() => [
  {
    component: SecondaryButton,
    props: { size: 'large', class: 'w-100', text: t('cancel') },
    cols: '12',
    md: '6',
    onClick: () => { cancelDialog('jsonInput'); }
  },
  {
    component: PrimaryButton,
    props: { size: 'large', class: 'w-100', text: t('confirm') },
    cols: '12',
    md: '6',
    onClick: () => { processJson(); }
  }
]);
const jsonSummaryButtons = computed(() => [
  {
    component: SecondaryButton,
    props: { size: 'large', class: 'w-100', text: t('cancel') },
    cols: '12',
    md: '6',
    onClick: () => { cancelDialog('jsonSummary'); }
  },
  {
    component: PrimaryButton,
    props: { size: 'large', class: 'w-100', text: t('confirm'), disabled: !(jsonSummaryData.value?.validEntries?.length > 0) },
    cols: '12',
    md: '6',
    onClick: () => { confirmJsonSummary(); }
  }
]);

/**
 * Return unique communities for selected metering points.
 *
 * @param {Object} requestEnergyData - Selected metering points and start/end dates from date picker.
 * @returns {Array} - Unique communities for selected metering points.
 */
function uniqueECN(requestEnergyData) {
  // Handle null or undefined input
  if (!requestEnergyData || !requestEnergyData.meteringPointsData) {
    return [];
  }

  const ecnList = [];
  for (const mp of requestEnergyData.meteringPointsData) {
    if (!ecnList.includes(mp.ecn)) {
      ecnList.push(mp.ecn);
    }
  }
  return ecnList;
}

/**
 * Format start date for energy data summary.
 *
 * Date format: dd-mm-yyyy
 *
 * @type {ComputedRef<string>}
 */
const formattedGetEnergyStartDate = computed(() => {
  if (!getEnergyDataStartDate.value) return null;
  const date = new Date(getEnergyDataStartDate.value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
  }
);

/**
 * Format end date for energy data summary.
 *
 * Date format: dd-mm-yyyy
 *
 * @type {ComputedRef<string>}
 */
const formattedGetEnergyEndDate = computed(() => {
  if (!getEnergyDataEndDate.value) return null;
  const date = new Date(getEnergyDataEndDate.value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
  }
);

/**
 * Start get energy data process.
 *
 * @returns {void}
 */
async function confirmGetEnergyData() {
  // Close dialog
  confirmGetEnergyDataDialog.value = false;
  // Start service
  const callsResult = await startEnergyDataRequest(requestEnergyData.value);

  $log.debug('confirmGetEnergyData call stats', callsResult);
  $log.debug('confirmGetEnergyData requestEnergyData', requestEnergyData.value);

  /// @todo display stats if succeeded is not calles wanted?
}

function clearJsonInput() {
  jsonInput.value = '';
  jsonValidationError.value = '';
}

const placeHolderText = ref("[{\"meteringPoint\": \"AT0030000000000000000000000000001\", \"dateFrom\": \"2024-01-01\", \"dateTo\": \"2024-01-31\", \"ecn\": \"RC123456\", \"ecid\": \"ECID123\"}]")

</script>

<template>
  <v-container class="pa-0">


    <p v-if="props.showHeader" class="textHeadline">{{ t('meteringPointsCm') }}</p>

    <DialogFlexibleInput
      :model-value="overlayInfoActive"
      @update:modelValue="overlayInfoActive = $event"
      :title="t('details')"
      :message="overlayInfoMessage"
      :buttonsConfig="overlayButtons"
    />

    <!--    :custom-filter="filterCommunities"-->
    <div class="filterRow d-flex flex-column align-center flex-md-row justify-lg-space-between w-100"
      :class="{'mx-5': props.clientCodeFilter && $vuetify.display.xs}">

        <v-autocomplete class="w-100"  v-if="!props.clientCodeFilter" bg-color="white" :class="{'mr-0': $vuetify.display.xs}" hide-details
          :label="t('community')" v-model="filterCompany.ecnList" :items="communityAvailable"
          :filter-keys="['title', 'value']" multiple variant="outlined" item-value="ecn" clearable></v-autocomplete>


        <v-autocomplete class="w-100" bg-color="white" :class="{'mr-0': $vuetify.display.xs}" :label="t('status')" hide-details
          v-model="filterPaging.status" :items="statusAvailable" :filter-keys="['title', 'value']" multiple
          variant="outlined" clearable></v-autocomplete>


        <v-text-field class="w-100" bg-color="white" :class="{'mr-0': $vuetify.display.xs}" hide-details
          :label="t('descriptionOrMemberOrMeteringPoint')" v-model="filterPaging.search" variant="outlined"
           ></v-text-field>
      <!--      Get Energy Data Button-->

        <div class="d-flex flex-column flex-md-row ga-2 w-100">
          <PrimaryButton size="default" prepend-icon="mdi-plus" @click="buttonGetEnergyData" :text="t('getEnergyData.buttonLabel')" />
          <SecondaryButton size="default" prepend-icon="mdi-plus" @click="jsonButtonGetEnergyData" :text="t('getEnergyData.jsonButtonLabel')" />
        </div>

    </div>

    <!--    Get Energy Data Dates Dialog -->
    <DialogFlexibleInput
      :model-value="selectDateDialog"
      @update:modelValue="selectDateDialog = $event"
      :title="t('getEnergyData.dateRangeDialog')"
      :buttonsConfig="dateDialogButtons"
      :persistent="true"
    >
      <template #input-slot>
        <div class="mb-2">{{ t('dateRange') }}</div>
        <DateRangePicker
          :dateRange="energyDataDateRange"
          :placeholder="t('dateRange')"
          @update:dateRange="(val) => energyDataDateRange = val"
        />
      </template>
    </DialogFlexibleInput>

    <!--    Energy Data summary and confirm -->
    <DialogFlexibleInput
      :model-value="confirmGetEnergyDataDialog"
      @update:modelValue="confirmGetEnergyDataDialog = $event"
      :title="t('getEnergyData.requestSummary')"
      :buttonsConfig="energySummaryButtons"
    >
      <template #input-slot>
        <div>
          <p>{{ t('getEnergyData.jsonSummaryDialogStartDate') }}: {{ formattedGetEnergyStartDate }}</p>
          <p>{{ t('getEnergyData.jsonSummaryDialogEndDate') }}: {{ formattedGetEnergyEndDate }}</p>
          <br>
          <p>{{ t('getEnergyData.communitiesSelected') }}</p>
          <ul>
            <li v-for="ec in uniqueECN(requestEnergyData)" :key="ec">{{ ec }}</li>
          </ul>
          <br>
          <p><u>{{ t('community') }} - {{ t('meteringPoints') }}:</u></p>
          <ul>
            <li v-for="mp in requestEnergyData.meteringPointsData" :key="mp.mpmObjectId">{{ mp.ecn }} - {{ mp.meteringPointShort }}</li>
          </ul>
        </div>
      </template>
    </DialogFlexibleInput>

    <!--    Energy Data json input dialog -->
    <DialogFlexibleInput
      :model-value="jsonInputDialog"
      @update:modelValue="jsonInputDialog = $event"
      :title="t('getEnergyData.jsonInputDialog')"
      :buttonsConfig="jsonInputButtons"
    >
      <template #input-slot>
        <v-textarea
          v-model="jsonInput"
          :label="t('getEnergyData.jsonInputDialogLabel')"
          :placeholder="placeHolderText"
          :error-messages="jsonValidationError"
          rows="10"
          variant="outlined"
        ></v-textarea>
        <div class="d-flex justify-end mt-2">
          <v-btn
            size="small"
            variant="text"
            @click="clearJsonInput"
            :disabled="!jsonInput"
          >
            {{ t('getEnergyData.jsonClearInput') }}
          </v-btn>
        </div>
      </template>
    </DialogFlexibleInput>

    <!-- JSON Summary Dialog -->
    <DialogFlexibleInput
      :model-value="jsonSummaryDialog"
      @update:modelValue="jsonSummaryDialog = $event"
      :title="t('getEnergyData.jsonSummaryTitle')"
      :buttonsConfig="jsonSummaryButtons"
    >
      <template #input-slot>
        <div>
          <div class="mb-4">
            <strong>{{ t('getEnergyData.meteringPointsCount') }}:</strong>
            {{ jsonSummaryData?.validEntries.length || 0 }}
          </div>

          <div class="mb-4">
            <strong>{{ t('getEnergyData.affectedCommunities') }}:</strong>
            <ul>
              <li v-for="ecn in [...new Set(jsonSummaryData?.validEntries.map(data => data.meteringPointsData[0].ecn) || [])]" :key="ecn">
                {{ ecn }}
              </li>
            </ul>
          </div>

          <div class="mb-4">
            <strong>{{ t('getEnergyData.meteringPoints') }}:</strong>
            <ul>
              <li v-for="data in jsonSummaryData?.validEntries || []" :key="data.meteringPointsData[0].mpmObjectId">
                {{ data.meteringPointsData[0].ecn }} - {{ data.meteringPointsData[0].meteringPointShort }}
                <br>
                <span class="text-caption">
                  {{ t('getEnergyData.jsonSummaryDialogStartDate') }}: {{ data.dateStart }} -
                  {{ t('getEnergyData.jsonSummaryDialogEndDate') }}: {{ data.dateEnd }}
                </span>
              </li>
            </ul>
          </div>

          <!-- Not Found Metering Points Section -->
          <div v-if="jsonSummaryData?.notFoundMeteringPoints && jsonSummaryData.notFoundMeteringPoints.length > 0" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="d-flex align-center mb-2">
              <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
              <strong>{{ t('getEnergyData.notFoundMeteringPoints') }} ({{ jsonSummaryData.notFoundMeteringPoints.length }}):</strong>
            </div>

            <ul>
              <li v-for="(mp, index) in jsonSummaryData.notFoundMeteringPoints" :key="index" class="text-error">
                {{ mp.meteringPoint }} ({{ t('getEnergyData.ecn') }}: {{ mp.ecn }}, {{ t('getEnergyData.ecid') }}: {{ mp.ecid }})
              </li>
            </ul>
          </div>
        </div>
      </template>
    </DialogFlexibleInput>

    <div
      :class="{'pa-0 bg-transparent': !props.clientCodeFilter && $vuetify.display.xs}">

      <v-data-table @click:row="handleRowClick" class="hidden-sm-and-down elevation-2 mainViewContent" mobile-breakpoint="sm" :headers="tableHeader"
        :items="membershipsDisplay" :search="filterPaging.search" v-model:page="filterPaging.page"
        v-model:items-per-page="filterPaging.itemsPerPage" item-value="mpmObjectId" show-select
        v-model="selectedMeteringPoints">
        <template v-slot:[`item.membershipStatus`]="{ item, value }">
          <v-chip :color="getChipColor(value)" size="small" variant="tonal" @click.stop="showDetails(item)">
            {{ getChipText(value) }}
            <v-tooltip style="color: white" activator="parent" location="start">
              <p>{{ getChipTooltip(value, item) }}</p>
            </v-tooltip>
          </v-chip>
        </template>
        <template v-slot:[`item.meteringPointShort`]="{ item, value }">
          <span @click.stop="copyText(item.meteringPoint)" class="hovercell">
            {{ value }}
            <v-icon size="tiny" :title="t('clickToCopy')" class="hovercopy hoverpos">mdi-content-copy</v-icon>
          </span>
        </template>
      </v-data-table>

      <v-data-table class="hidden-md-and-up bg-transparent" :headers="tableHeader" :items="membershipsDisplay"
        :search="filterPaging.search" v-model:page="filterPaging.page"
        v-model:items-per-page="filterPaging.itemsPerPage">
        <template v-slot:headers></template>
        <template v-slot:item="{ item }">
          <v-card @click="handleCardClick(item)" elevation="2" class="mb-3 mt-2 ml-2 mr-2" min-height="110">
            <v-row class="pt-2 pl-2 h-40" no-gutters>
              <v-col cols="1">
                <v-checkbox v-model="selectedMeteringPoints" :value="item.mpmObjectId"></v-checkbox>
              </v-col>
              <v-col cols="8" class="font-weight-medium">{{ item.nameFriendly }}</v-col>
              <v-col cols="3" class=" text-right pr-3">
                <v-chip :color="getChipColor(item.membershipStatus)" size="small" variant="tonal">
                  {{ getChipText(item.membershipStatus) }}
                </v-chip>
              </v-col>
            </v-row>
            <v-row class="pt-2 pl-2 h-40" no-gutters>
              <v-col cols="9" class=" font-weight-medium">{{ item.memberAssociated?item.memberAssociated.nameFriendly:""
                }}</v-col>
              <v-col cols="3" class=" text-right pr-3">
                {{ item.meteringpointDirectionDisplay }}
              </v-col>
            </v-row>
            <v-row class="pl-2 h-40 pt-3" no-gutters>
              <v-col cols="7">
                <span @click.stop="copyText(item.meteringPoint)" class="hovercell">
                  {{ item.meteringPointShort }}
                  <v-icon size="tiny" :title="t('clickToCopy')" class="hovercopy hoverpos">mdi-content-copy</v-icon>
                </span>
              </v-col>
              <v-col cols="5" class="text-right pr-2 pb-2">
                {{ item.dateSpan }}
              </v-col>
            </v-row>
          </v-card>
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>


<style scoped>

.v-data-table :deep(tr.v-data-table__tr:hover) {
  background: #DEECF7 !important;
}

.filterRow {
  margin: 16px 0;
}

</style>
